import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, takeUntil, tap, startWith, finalize } from 'rxjs/operators';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import {
  MixModeType,
  OutgoingMessageType,
  StreamingForwardMessage,
  StreamingMixModeMessage,
  StreamingStopForwardingMessage,
} from 'messages/websocket.messages';

import { DEFAULT_MIX_VOLUME } from '../../constants/volume';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { RtcStatsStreamService } from '../rtc-stats-stream';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { EasingService, linearEasing } from './../easing-service';
import { MixSubscriber } from './mix-subscriber';

/**
 * Service for managing the audio mix subscription.
 */
@Injectable({
  providedIn: 'root',
})
export class PresentationAudioService extends Loggable {
  private videoRoomService = inject(VideoRoomService);
  private websocketService = inject(WebsocketService);
  private rtcStatsStreamService = inject(RtcStatsStreamService);
  private easingService = inject(EasingService);
  private store = inject<Store<AppState>>(Store);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private isInitialized = false;
  private isAudioForwarding = false;
  private mixSubscriber?: MixSubscriber;
  private mixVolume = DEFAULT_MIX_VOLUME;
  private pendingTransition?: Subscription;

  // Presentation state machine to prevent race conditions
  private presentationStateSubject = new BehaviorSubject<'inactive' | 'starting' | 'active' | 'stopping'>('inactive');
  private presentationState$ = this.presentationStateSubject.asObservable();

  // Audio forwarding state machine
  private audioForwardingStateSubject = new BehaviorSubject<'inactive' | 'starting' | 'active' | 'stopping'>(
    'inactive',
  );
  private audioForwardingState$ = this.audioForwardingStateSubject.asObservable();

  public constructor() {
    super();
    this.setDisplayName('PresentationAudioService');
  }

  public init(): void {
    if (this.isInitialized) {
      this.warn('Already initialized, ignoring duplicate init call');
      return;
    }

    this.info('Initializing');
    this.isInitialized = true;
    this.destroyed$ = new ReplaySubject(1);

    // Handle presentation state changes with proper state machine
    this.store
      .select(socialAppSelectors.isPresentationActiveAndPublishing)
      .pipe(startWith(false), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        const currentState = this.presentationStateSubject.getValue();

        if (isPresentationActive && (currentState === 'inactive' || currentState === 'stopping')) {
          this.presentationStateSubject.next('starting');
          this.onStartPresentation();
        } else if (!isPresentationActive && (currentState === 'active' || currentState === 'starting')) {
          this.presentationStateSubject.next('stopping');
          this.onStopPresentation();
        }
      });

    // Handle audio forwarding state changes
    this.store
      .select(socialAppSelectors.isUserAudioInMix)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((isUserAudioInMix) => {
        const currentState = this.audioForwardingStateSubject.getValue();

        if (isUserAudioInMix && (currentState === 'inactive' || currentState === 'stopping')) {
          this.audioForwardingStateSubject.next('starting');
          this.forwardAudio();
        } else if (!isUserAudioInMix && (currentState === 'active' || currentState === 'starting')) {
          this.audioForwardingStateSubject.next('stopping');
          this.stopAudioForwarding();
        }
      });

    // Handle volume changes safely
    this.store
      .select(socialAppSelectors.getMixVolume)
      .pipe(
        distinctUntilChanged(),
        tap((volume) => (this.mixVolume = volume)),
        takeUntil(this.destroyed$),
      )
      .subscribe((volume) => {
        if (this.mixSubscriber && this.presentationStateSubject.getValue() === 'active') {
          this.mixSubscriber.setVolume(volume);
        }
      });
  }

  public enableCrossRoomMix(): void {
    if (this.presentationStateSubject.getValue() !== 'active') {
      this.warn('Cannot enable cross room mix - presentation not active');
      return;
    }

    const msg: StreamingMixModeMessage = {
      t: OutgoingMessageType.STR_MIX,
      mode: MixModeType.CROSS_ROOM,
    };
    this.websocketService.sendMessage(msg);
  }

  public disableCrossRoomMix(): void {
    if (this.presentationStateSubject.getValue() !== 'active') {
      this.warn('Cannot disable cross room mix - presentation not active');
      return;
    }

    const msg: StreamingMixModeMessage = {
      t: OutgoingMessageType.STR_MIX,
      mode: MixModeType.IN_ROOM,
    };
    this.websocketService.sendMessage(msg);
  }

  private onStartPresentation(): void {
    this.info('Presentation starting');

    try {
      // Reset volume to default
      this.store.dispatch(socialAppActions.SET_MIX_VOLUME({ volume: DEFAULT_MIX_VOLUME }));
      this.mixVolume = DEFAULT_MIX_VOLUME;

      // Create mix subscriber
      if (!this.mixSubscriber) {
        this.mixSubscriber = new MixSubscriber(
          this.videoRoomService,
          this.websocketService,
          this.rtcStatsStreamService,
          this.store,
          this.mixVolume,
          this.videoRoomService.mixStream,
        );
      }

      this.presentationStateSubject.next('active');
      this.info('Presentation started successfully');
    } catch (error) {
      this.error('Failed to start presentation', error);
      this.presentationStateSubject.next('inactive');

      // Clean up if initialization fails
      if (this.mixSubscriber) {
        this.mixSubscriber.destroy();
        this.mixSubscriber = undefined;
      }
    }
  }

  private onStopPresentation(): void {
    this.info('Stopping presentation');

    try {
      // Stop audio forwarding if active
      const audioForwardingState = this.audioForwardingStateSubject.getValue();
      if (audioForwardingState === 'active' || audioForwardingState === 'starting') {
        this.stopAudioForwarding();
      }

      // Cancel any pending transitions
      if (this.pendingTransition) {
        this.pendingTransition.unsubscribe();
        this.pendingTransition = undefined;
      }

      // Clean up mix subscriber
      if (this.mixSubscriber) {
        this.mixSubscriber.destroy();
        this.mixSubscriber = undefined;
      }

      this.presentationStateSubject.next('inactive');
      this.info('Presentation stopped successfully');
    } catch (error) {
      this.error('Error stopping presentation', error);
      // Force inactive state even if there was an error
      this.presentationStateSubject.next('inactive');
    }
  }

  private updateMixVolume(value: number): void {
    if (value >= 0 && value <= DEFAULT_MIX_VOLUME) {
      this.store.dispatch(socialAppActions.SET_MIX_VOLUME({ volume: value }));
      this.mixVolume = value;
    }
  }

  public async transitionFadeOutAudio(delay: number): Promise<void> {
    // Cancel any existing transition
    if (this.pendingTransition) {
      this.pendingTransition.unsubscribe();
      this.pendingTransition = undefined;
    }

    const currentState = this.presentationStateSubject.getValue();
    // Allow transition during active or stopping states to avoid race conditions during room changes
    if (currentState !== 'active' && currentState !== 'stopping') {
      this.warn(`Cannot fade audio - presentation state is ${currentState}`);
      return;
    }

    this.info(`Transition audio: Starting transition (presentation state: ${currentState})`);

    try {
      // Use a local subject to handle cancellation
      const transitionCancelled$ = new Subject<void>();

      this.pendingTransition = this.easingService
        .createEasing(DEFAULT_MIX_VOLUME, 0, delay, 10, linearEasing)
        .pipe(
          takeUntil(transitionCancelled$),
          takeUntil(this.destroyed$),
          finalize(() => {
            this.updateMixVolume(0);
            this.pendingTransition = undefined;
            transitionCancelled$.complete();
          }),
        )
        .subscribe((value) => {
          this.updateMixVolume(value);
        });

      // Convert to Promise for the async/await API
      await new Promise<void>((resolve) => {
        this.pendingTransition?.add(() => resolve());
      });

      this.info('Transition audio: Completed');
    } catch (error) {
      this.error('Failed to transition audio', error);
      throw error;
    }
  }

  public resetFadedAudioMix(): void {
    // Cancel any active transition
    if (this.pendingTransition) {
      this.pendingTransition.unsubscribe();
      this.pendingTransition = undefined;
    }

    this.info('Transition audio: resetting mix volume');
    this.updateMixVolume(DEFAULT_MIX_VOLUME);
  }

  /**
   * Indicate to the server that this user's audio should be forwarded to the presentation mix.
   */
  private forwardAudio(): void {
    if (this.isAudioForwarding) {
      this.warn('Already forwarding audio, ignoring duplicate request');
      return;
    }

    this.info('Forwarding audio');
    try {
      // Forward my audio to the mix
      const forwardMsg: StreamingForwardMessage = {
        t: OutgoingMessageType.STR_FWD,
      };
      this.websocketService.sendMessage(forwardMsg);
      this.isAudioForwarding = true;
      this.audioForwardingStateSubject.next('active');
    } catch (error) {
      this.error('Failed to forward audio', error);
      this.audioForwardingStateSubject.next('inactive');
    }
  }

  /**
   * Indicate to the server that this user's audio should be not be forwarded to the presentation mix.
   */
  private stopAudioForwarding(): void {
    if (!this.isAudioForwarding) {
      this.warn('Audio not forwarding, ignoring stop request');
      return;
    }

    this.info('Stopping audio forwarding');
    try {
      const stopForwarding: StreamingStopForwardingMessage = {
        t: OutgoingMessageType.STR_STOP_FWD,
      };
      this.websocketService.sendMessage(stopForwarding);
      this.isAudioForwarding = false;
      this.audioForwardingStateSubject.next('inactive');
    } catch (error) {
      this.error('Failed to stop audio forwarding', error);
      // Force reset state even if there was an error
      this.isAudioForwarding = false;
      this.audioForwardingStateSubject.next('inactive');
    }
  }

  public get subscriber(): MixSubscriber | undefined {
    return this.mixSubscriber;
  }

  public get isPresentationActive(): boolean {
    return this.presentationStateSubject.getValue() === 'active';
  }

  /**
   * Deallocate presentation resources
   */
  public destroy(): void {
    if (!this.isInitialized) {
      return;
    }

    this.isInitialized = false;

    // Cancel any pending transitions
    if (this.pendingTransition) {
      this.pendingTransition.unsubscribe();
      this.pendingTransition = undefined;
    }

    // Clean up mix subscriber
    if (this.mixSubscriber) {
      this.mixSubscriber.destroy();
      this.mixSubscriber = undefined;
    }

    // Reset state machines
    this.presentationStateSubject.next('inactive');
    this.audioForwardingStateSubject.next('inactive');
    this.isAudioForwarding = false;

    // Complete all observables
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
